/**
 * Habit execution v2 API (requires HABIT_EXECUTION_V2 on server).
 */
import { apiFetch } from '../config/api'

export interface HabitOccurrenceDetail {
  id: number
  habit_definition_id: number
  habit_node_id: number
  status: string
  scheduled_start_at: string
  scheduled_end_at?: string | null
  progress_percent?: number | null
  feeling_before?: string | null
  feeling_after?: string | null
  habit_title?: string
  action_items?: Array<Record<string, unknown>>
  notes?: Array<Record<string, unknown>>
}

async function jsonOrThrow<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const t = await resp.text().catch(() => '')
    throw new Error(`habit_execution ${resp.status}: ${t.slice(0, 200)}`)
  }
  return (await resp.json()) as T
}

export const habitExecutionService = {
  async getOccurrence(id: number): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}`)
    const data = await jsonOrThrow<{ success: boolean; occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async start(id: number): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/start`, {
      method: 'POST',
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async stop(id: number): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/stop`, {
      method: 'POST',
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async complete(id: number, partial = false): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ partial }),
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async skip(id: number): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/skip`, {
      method: 'POST',
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async addNote(id: number, content: string): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async addVoiceNote(
    id: number,
    payload: {
      storage_url: string
      transcript_text?: string
      mime_type?: string
      duration_seconds?: number
    },
  ): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/voice-note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async updateFeelings(
    id: number,
    feeling_before?: string,
    feeling_after?: string,
  ): Promise<HabitOccurrenceDetail> {
    const resp = await apiFetch(`/api/habit-execution/v1/occurrences/${id}/feelings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feeling_before, feeling_after }),
    })
    const data = await jsonOrThrow<{ occurrence: HabitOccurrenceDetail }>(resp)
    return data.occurrence
  },

  async backfill(): Promise<{ count: number }> {
    const resp = await apiFetch('/api/habit-execution/v1/backfill', { method: 'POST' })
    const data = await jsonOrThrow<{ count: number }>(resp)
    return { count: data.count }
  },
}
